import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TablePagination, Paper, Chip, IconButton, Tooltip, TextField,
  InputAdornment, FormControl, Select, MenuItem, InputLabel,
  CircularProgress, Typography, Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { travelerScreeningAPI } from '../../services/api';
import HealthPassCard from './HealthPassCard';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';

// ── Design tokens (mirrors DashboardPage.tsx / ContactsPage.tsx) ─────────────
const C = {
  card: "#ffffff",
  cardBorder: "#e2e8f0",
  ink: "#0f1f35",
  sub: "#475569",
  muted: "#94a3b8",
  headerBg: "#f8fafc",
};
const S = {
  sm: "0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04)",
};

interface Screening {
  id: number;
  full_name?: string;
  id_number?: string;
  nationality?: string;
  entry_country?: string;
  arrival_date?: string;
  risk_level?: string;
  status?: string;
  temperature?: number;
  pass_record?: Record<string, unknown>;
  created_at?: string;
}

interface Props {
  refresh: number;
  onAddNew: () => void;
  onScanQr?: () => void;
}

const PassengerList: React.FC<Props> = ({ refresh, onAddNew, onScanQr }) => {
  const [rows,      setRows]      = useState<Screening[]>([]);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(0);
  const [rowsPerPage] = useState(20);
  const [search,    setSearch]    = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filter,    setFilter]    = useState('');
  const [dateFrom,  setDateFrom]  = useState('');
  const [dateTo,    setDateTo]    = useState('');
  const [loading,   setLoading]   = useState(false);
  const [viewPass,  setViewPass]  = useState<Screening | null>(null);
  const [viewPassToken, setViewPassToken] = useState<string | null>(null);

  const openViewPass = (row: Screening) => {
    setViewPass(row);
    setViewPassToken(null);
    travelerScreeningAPI.getPassToken(row.id)
      .then((data: any) => setViewPassToken(data?.token || null))
      .catch(() => setViewPassToken(null));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await travelerScreeningAPI.listScreenings({
        skip:       page * rowsPerPage,
        limit:      rowsPerPage,
        risk_level: filter || undefined,
        search:     debouncedSearch.trim() || undefined,
        date_from:  dateFrom || undefined,
        date_to:    dateTo || undefined,
      });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filter, debouncedSearch, dateFrom, dateTo, refresh]);

  useEffect(() => { load(); }, [load]);

  // Search runs server-side across all records; wait for typing to pause.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Any filter change starts back at the first page.
  useEffect(() => { setPage(0); }, [filter, debouncedSearch, dateFrom, dateTo]);

  const hasFilters = Boolean(search || filter || dateFrom || dateTo);

  const riskChip = (level?: string) => (
    <Chip
      size="small"
      label={level === 'cleared' ? 'Cleared' : level === 'high_risk' ? 'High Risk' : 'Pending'}
      color={level === 'cleared' ? 'success' : level === 'high_risk' ? 'error' : 'default'}
      sx={{ fontWeight: 700, borderRadius: '8px' }}
    />
  );

  const handleDownload = (id: number) => {
    const url = travelerScreeningAPI.getPassPdfUrl(id);
    fetch(url, { credentials: 'include' })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `health_pass_${id}.pdf`;
        a.click();
      });
  };

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search name, passport, nationality…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: C.muted }} /></InputAdornment> }}
          sx={{ minWidth: 260, bgcolor: C.card, borderRadius: '10px', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
        />
        <FormControl size="small" sx={{ minWidth: 160, bgcolor: C.card, borderRadius: '10px', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
          <InputLabel>Risk Level</InputLabel>
          <Select value={filter} label="Risk Level" onChange={e => setFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="cleared">Cleared</MenuItem>
            <MenuItem value="high_risk">High Risk</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          type="date"
          label="Created from"
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: dateTo || undefined }}
          sx={{ minWidth: 160, bgcolor: C.card, borderRadius: '10px', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
        />
        <TextField
          size="small"
          type="date"
          label="Created to"
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: dateFrom || undefined }}
          sx={{ minWidth: 160, bgcolor: C.card, borderRadius: '10px', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
        />
        {hasFilters && (
          <Button
            onClick={() => { setSearch(''); setDebouncedSearch(''); setFilter(''); setDateFrom(''); setDateTo(''); }}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Clear filters
          </Button>
        )}
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1.5 }}>
          {onScanQr && (
            <Button
              variant="outlined"
              startIcon={<QrCodeScannerIcon />}
              onClick={onScanQr}
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, borderColor: '#1f93ff', color: '#1f93ff' }}
            >
              Scan Health Pass QR
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={onAddNew}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, boxShadow: S.sm }}
          >
            Add Client
          </Button>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: '16px', borderColor: C.cardBorder, boxShadow: S.sm, overflow: 'hidden' }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: C.headerBg }}>
              <TableCell sx={{ fontWeight: 700, color: C.sub, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700, color: C.sub, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>Full Name</TableCell>
              <TableCell sx={{ fontWeight: 700, color: C.sub, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>Passport No.</TableCell>
              <TableCell sx={{ fontWeight: 700, color: C.sub, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>Nationality</TableCell>
              <TableCell sx={{ fontWeight: 700, color: C.sub, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>Entry Country</TableCell>
              <TableCell sx={{ fontWeight: 700, color: C.sub, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>Arrival Date</TableCell>
              <TableCell sx={{ fontWeight: 700, color: C.sub, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>Temp (°C)</TableCell>
              <TableCell sx={{ fontWeight: 700, color: C.sub, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>Risk</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, color: C.sub, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography sx={{ color: C.muted }}>
                    {hasFilters ? 'No screenings match these filters.' : 'No screenings yet.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ '&:last-child td': { borderBottom: 0 }, '& td': { color: C.ink } }}
                >
                  <TableCell sx={{ color: C.muted }}>{row.id}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{row.full_name || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{row.id_number || '—'}</TableCell>
                  <TableCell>{row.nationality || '—'}</TableCell>
                  <TableCell>{row.entry_country || '—'}</TableCell>
                  <TableCell>{row.arrival_date || '—'}</TableCell>
                  <TableCell sx={{ color: (row.temperature ?? 0) >= 38 ? 'error.main' : 'inherit', fontWeight: (row.temperature ?? 0) >= 38 ? 'bold' : 'normal' }}>
                    {row.temperature != null ? `${row.temperature}°C` : '—'}
                  </TableCell>
                  <TableCell>{riskChip(row.risk_level)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Pass">
                      <IconButton size="small" onClick={() => openViewPass(row)} disabled={!row.pass_record}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download PDF">
                      <IconButton size="small" onClick={() => handleDownload(row.id)} disabled={!row.pass_record}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPageOptions={[20]}
          sx={{ borderTop: `1px solid ${C.cardBorder}` }}
        />
      </TableContainer>

      {/* Pass preview dialog */}
      <Dialog open={Boolean(viewPass)} onClose={() => setViewPass(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Health Pass — {viewPass?.full_name}</DialogTitle>
        <DialogContent>
          {viewPass?.pass_record && (
            <HealthPassCard passRecord={{ ...(viewPass.pass_record as any), pass_token: viewPassToken ?? undefined } as unknown as Parameters<typeof HealthPassCard>[0]['passRecord']} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewPass(null)}>Close</Button>
          {viewPass && (
            <Button startIcon={<DownloadIcon />} onClick={() => handleDownload(viewPass.id)}>
              Download PDF
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PassengerList;
